import React from 'react';
import { Image, Space } from 'antd';

/**
 * 建联跟进记录的**共享解析与渲染件**（2.1 需求点7）。
 *
 * 按 `DriverFeedbackDetail` 的口径新写的一份，**当前只有 `StationFollowUpTimeline` 用**：
 * - 「司机反馈跟进」详情 —— 阶段化卡片 + 可操作（二次建联表单等），仍用它自己那份 local 实现
 * - 「查看揽收站点」详情 —— **只读**跟进记录（本次新增，仅 link_status=5 展示），用本文件
 *
 * ⚠️ 三个 content 类型、`PICKUP_FAILURE_CODE`、`OnsiteProofs` 在 `DriverFeedbackDetail`
 * 里各有一份 local 副本（未改造成 import，避免为本需求去动那个 642 行带 mutation 的组件）。
 * **后端改 operation_log.content 结构时必须同步改两处**，只改一处会让另一处静默解析出空值。
 *
 * 抽取范围刻意只含「解析 + 无状态渲染」，不含任何操作/表单逻辑：
 * 那部分与「司机反馈跟进」的业务流程强耦合，搬进只读详情既用不上也会带进一堆 mutation 依赖。
 */

/** op_type=3 首次建联的 content 结构 */
export type FirstContactContent = {
    result?: 'contacted' | 'not_contacted';
    proofS3Keys?: string[];
};

/** op_type=5 司机上门反馈的 content 结构 */
export type OnsiteContent = {
    state?: 'dispatched' | 'success' | 'failed';
    onsiteTime?: string;
    driverName?: string;
    driverPhone?: string;
    planTime?: string;
    taskNo?: string;
    proofS3Keys?: string[];
    onsiteContactName?: string;
    onsiteContactPhone?: string;
    note?: string;
};

/** op_type=6 二次建联的 content 结构 */
export type SecondContactContent = {
    result?: 'contacted' | 'not_contacted';
    groupName?: string;
    groupId?: string;
    proofS3Keys?: string[];
};

/** 司机端揽收失败原因码（mobile-web `enums/PickupEnum.ts` pickupFailureReasons）；note 为纯码时不展示 */
export const PICKUP_FAILURE_CODE = /^ERPU\d{3}$/;

// fmtTime 已移除（2026-09-10）：它用裸 `new Date(iso)` 把服务端的 UTC 无后缀串按浏览器本地解析，
// 等于把 UTC 当本地打印。跟进记录一族统一改用 hubTime.formatHubTimeFull(iso, hubZone)。
// 刻意不保留成 deprecated 别名——留着下一个人还会用它。

/**
 * `operation_log.content` → 结构化对象。
 *
 * ⚠️ **content 的格式不统一**：op_type 3/5/6 写的是 JSON（对应上面三个类型），
 * 而 RETIRE/MERGE 等写的是纯文本（例如「站点退役：AB地址映射把本站点 N 个关联地址…」）。
 * 故先看首字符是不是 `{`，不是就直接返回 null，由调用方按纯文本渲染 —— 不要 try/catch
 * 硬解析，那样纯文本也会走一遍异常路径。
 */
export function parseContent<T>(raw?: string | null): T | null {
    if (!raw) return null;
    const s = raw.trim();
    if (!s.startsWith('{')) return null;
    try {
        return JSON.parse(s) as T;
    } catch {
        return null;
    }
}

/**
 * 现场照片。
 *
 * 优先用后端预签好的 `urls` 直接 `<img>`；只有 `keys`（没预签成功）时退化成占位块 ——
 * 有几张就显示几个占位，让运营知道"有照片但没加载出来"，而不是当作没照片。
 */
export const OnsiteProofs: React.FC<{ keys?: string[]; urls?: string[]; label: string }> = ({
    keys,
    urls,
    label,
}) => {
    if (urls && urls.length) {
        return (
            <Image.PreviewGroup>
                <Space size={8} wrap>
                    {urls.map((u, i) => (
                        <Image key={i} src={u} width={96} height={72} style={{ objectFit: 'cover', borderRadius: 8 }} />
                    ))}
                </Space>
            </Image.PreviewGroup>
        );
    }
    return keys && keys.length ? (
        <Space size={8} wrap>
            {keys.map((_, i) => (
                <div
                    key={i}
                    className="flex h-[72px] w-[96px] flex-col items-center justify-center rounded-lg bg-purple-50 text-purple-500"
                >
                    <span className="text-lg">📷</span>
                    <span className="mt-1 text-xs">
                        {label} {i + 1}
                    </span>
                </div>
            ))}
        </Space>
    ) : null;
};

/**
 * `pickup_station_operation_log.operation_type` → i18n key 后缀。
 *
 * 与后端 `StationOperationTypeEnum` 一一对应。取不到时返回 null，调用方回退成「操作 N」——
 * 后端加了新枚举而前端没跟上时，宁可显示原始码也不要显示空白（空白无从排查）。
 */
export const OP_TYPE_I18N_KEY: Record<number, string> = {
    1: 'opCreate',
    2: 'opUpdate',
    3: 'opFirstContact',
    4: 'opReviewPoolCreate',
    5: 'opOnsiteFeedback',
    6: 'opSecondContact',
    7: 'opLinkStatusChange',
    8: 'opSplit',
    9: 'opMerge',
    10: 'opAbMapping',
    11: 'opRetire',
    12: 'opAddressAbnormal',
};

/**
 * type=12「地址异常上报」的 content（2.2 需求点1）。
 *
 * <p>上报 / 编辑 / 撤销共用 type=12，靠 {@link action} 区分 —— 三者是同一件事的生命周期，
 * 时间线要按发生顺序连续展示，拆成三个 type 会让「先报后撤再报」变成三类互不相关的节点。
 */
export type AddressAbnormalContent = {
    action?: 'report' | 'edit' | 'revoke';
    reason?: string;
    remark?: string;
    proofS3Keys?: string[];
};

/**
 * 从时间线事件里取出「当前是否有有效的地址异常上报」（2.2 需求点1）。
 *
 * <p>判据 = 最新一条 type=12 的 action 不是 revoke。撤销后再上报会追加新记录，
 * 所以只看最新一条即可，不需要累计。
 *
 * <p><b>后端用的是同一份判据</b>（{@code PickupAddressAbnormalReportService.hasActiveStationReport}
 * 也取最新一条 type=12 的 action），刻意保持同源 —— 站点表上没有对应的状态列。
 * 曾经有过一列 {@code cw.address_abnormal_status} 专供后端闸门读，后按需求边界去掉：
 * 它与本日志信息重复，且两处各自演化会漂。改这里的判据时记得后端要一起改。
 */
export function activeAbnormalReport(
    // operationType 允许为 null：GraphQL 侧该字段可空（历史记录/异常数据），
    // 收窄成 number 会让调用方每次都得先过滤一遍，不如在这里按"不是 12 就跳过"处理。
    events: {
        // id 参与排序（见下方说明），不是可选装饰
        id?: number | null;
        operationType?: number | null;
        content?: string | null;
        time?: string | null;
        operatorName?: string | null;
    }[],
): { time?: string | null; operatorName?: string | null; content: AddressAbnormalContent } | null {
    // 按 id 而不是 time 取最新：`time` 来自 cw 的 created_at，生产是**无小数秒的 timestamp**（秒级）。
    // 同一秒内落的两条（两个运营在同秒分别点上报/撤销，Redis 锁只串行方法体、不覆盖事务提交，
    // 这是可达的）time 完全相同 ⇒ comparator 返回 0 ⇒ 稳定排序保留原顺序（后端按 id ASC 返回）
    // ⇒ 取到的是**较早**那条。而后端 findLatest 走 SQL 的 `ORDER BY id DESC`，取的是较晚那条 ——
    // 于是"是否有有效上报"前后端得出相反结论，且因为排序确定，刷新一百次都一样，不会自愈。
    // id 自增严格单调，是比秒级时间戳更准的发生先后，与后端同键。
    const latest = [...events]
        .filter((e) => e.operationType === 12)
        .sort((a, b) => (b.id ?? 0) - (a.id ?? 0))[0];
    if (!latest) return null;
    const content = parseContent<AddressAbnormalContent>(latest.content) ?? {};
    if (content.action === 'revoke') return null;
    return { time: latest.time, operatorName: latest.operatorName, content };
}
